import { RichTextAdvancedRender } from '@framework/engine/inputs/rich-text-advanced/render/RichTextAdvancedRender.tsx';
import { type IWidgetComponentProps } from '@framework/engine/schema/widgets/IWidgetType.ts';
import { getWidgetProps } from '@framework/engine/schema/widgets/repository/inputs/common/util/getWidgetProps.ts';
import { ConfigFormItem } from '@framework/engine/ui/form/ConfigFormItem.tsx';
import { useMemo } from 'react';
import { useUploadWidgetFieldName } from '../hooks/useUploadWidgetFieldName.ts';
import { useUploadWidgetHelpText } from '../hooks/useUploadWidgetHelpText.ts';
import { type IUploadWidget, UploadWidgetKind } from '../IUploadWidget.ts';
import { UploadWidgetInternalComponent } from './UploadWidgetInternalComponent.tsx';

export interface IUploadWidgetComponentInputProps
  extends IWidgetComponentProps<IUploadWidget> {
  /**
   * Se true, indica che l'input sarà per il file "pubblico",
   * necessario nel caso di widget con campi sensibili
   * (direttiva omissis true).
   */
  publicUpload?: boolean;

  attachmentUniqueId?: string;
}

export function UploadWidgetComponentInput(
  props: IUploadWidgetComponentInputProps,
) {
  const { widget, publicUpload, attachmentUniqueId } = props;
  const options = getWidgetProps(props);

  const isButton = widget.kind === UploadWidgetKind.Button;

  const label = publicUpload ? widget.publicUploadLabel : options.label;

  const { fieldName, filenameFieldName } = useUploadWidgetFieldName({
    publicUpload,
    options,
  });

  const helpText = useUploadWidgetHelpText({
    widget,
    options,
    publicUpload,
  });

  // Gestiamo nel formItem entrambi i campi dell'upload, sia il fileId che il filename
  // in questo modo riusciamo a gestire i default da reference
  const formItemNames = useMemo(
    () => [fieldName, filenameFieldName],
    [fieldName, filenameFieldName],
  );

  return (
    <ConfigFormItem
      name={formItemNames}
      label={<RichTextAdvancedRender text={label} />}
      help={isButton && <RichTextAdvancedRender text={helpText} />}
      layout={{
        hideVisualLabel: options.hideLabel,
      }}
      readonly={options.readonly}
      widget={props.widget}
    >
      <UploadWidgetInternalComponent
        widget={widget}
        isButton={isButton}
        options={options}
        publicUpload={publicUpload}
        attachmentUniqueId={attachmentUniqueId}
        readonly={options.readonly}
      />
    </ConfigFormItem>
  );
}
